<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>

    <div class="p-relative header-image pb-5"></div>

    <b-container class="custom-container mt-5">
      <b-row>
        <b-col cols="12" class="px-3">
          <candidates-tabs />

          <h4 class="font-weight-bold pt-4 pb-1 align-middle">Candidates</h4>
          <p class="text-muted mb-4">
            Every tradie you've engaged, grouped by what needs to happen next.
          </p>

          <div
            v-if="inProgressCandidates.length"
            class="in-progress-filters mb-4"
          >
            <b-form-input
              v-model="searchQuery"
              placeholder="Search by name or trade..."
              class="in-progress-filters__search"
            />
          </div>

          <template v-if="visiblePipelineSections.length">
            <div
              v-for="section in visiblePipelineSections"
              :key="section.key"
              class="pipeline-section mb-4"
            >
              <button
                type="button"
                class="pipeline-section__header"
                :class="`pipeline-section__header--${section.key}`"
                :aria-expanded="String(!!expandedSections[section.key])"
                @click="toggleSection(section.key)"
              >
                <b-icon
                  :icon="
                    expandedSections[section.key]
                      ? 'chevron-down'
                      : 'chevron-right'
                  "
                  class="pipeline-section__chevron"
                />
                <span class="pipeline-section__title">{{ section.title }}</span>
                <b-badge pill class="pipeline-section__count">
                  {{ section.candidates.length }}
                </b-badge>
                <span class="pipeline-section__hint">{{ section.hint }}</span>
              </button>

              <b-collapse :visible="!!expandedSections[section.key]">
                <b-row class="align-items-stretch candidates-grid-row pt-3">
                  <b-col
                    v-for="candidate in section.candidates"
                    :key="candidate.id"
                    cols="12"
                    sm="6"
                    lg="4"
                    class="mb-4"
                  >
                    <candidate-card
                      :candidate="candidate"
                      pipeline-view
                      plain-files
                      @view="goToCandidate"
                      @request-resume="openResumeRequestModal"
                      @invite-workforce="openWorkforceInvitationModal"
                      @apply-token="openTokenConfirmationModal"
                      @error="onError"
                    />
                  </b-col>
                </b-row>
              </b-collapse>
            </div>
          </template>

          <p
            v-else-if="!isLoading && inProgressCandidates.length"
            class="text-muted py-5 text-center"
          >
            No candidates match your search.
          </p>

          <p v-else-if="!isLoading" class="text-muted py-5 text-center">
            Nothing in your pipeline right now - resume requests you send and
            workforce invitations you extend will show up here.
          </p>

          <h4 class="font-weight-bold pt-5 pb-1 align-middle">Favourites</h4>
          <p class="text-muted mb-4">
            Tradies you've starred while browsing Open to Work.
          </p>

          <b-row
            v-if="favourites.length"
            class="align-items-stretch candidates-grid-row"
          >
            <b-col
              v-for="candidate in favourites"
              :key="candidate.id"
              cols="12"
              sm="6"
              lg="4"
              class="mb-4"
            >
              <candidate-card
                :candidate="candidate"
                plain-files
                @view="goToCandidate"
                @request-resume="openResumeRequestModal"
                @invite-workforce="openWorkforceInvitationModal"
                @apply-token="openTokenConfirmationModal"
                @error="onError"
              />
            </b-col>
          </b-row>

          <p v-else-if="!isLoading" class="text-muted py-5 text-center">
            No favourites yet - click the star on a candidate to save them here.
          </p>
        </b-col>
      </b-row>
    </b-container>

    <resume-request-modal
      :showModal="isRequestingResume"
      :candidate="candidateForModal"
      @closeModal="isRequestingResume = false"
      @sent="onResumeRequestSent"
    />

    <workforce-invitation-modal
      :showModal="isInvitingWorkforce"
      :candidate="candidateForModal"
      @closeModal="isInvitingWorkforce = false"
      @sent="onWorkforceInvitationSent"
    />

    <!-- The pending-token banner's "Review" link sends companies here, but
         until now there was nothing on this page that could actually apply
         a token — WorkforceInviteButton just showed a disabled label, and
         TokenConfirmationModal was only ever wired up from clicking the
         original acceptance notification (NotificationsList.vue), which is
         easy to lose track of. Same modal/action pair, reused here. -->
    <token-confirmation-modal
      v-if="showTokenConfirmationModal && canApplyOtwTokens"
      :showModal="showTokenConfirmationModal"
      :invitation="otwInvitationForModal"
      @closeModal="onTokenConfirmationModalClose"
      @apply="onApplyOtwToken"
      @dismiss="onDismissOtwToken"
      @purchaseTokens="onOtwTokenModalPurchaseTokens"
    />
    <purchase-token-modal
      v-if="canPurchaseTokens"
      :showModal="showPurchaseTokenModal"
      @closeModal="showPurchaseTokenModal = false"
      @purchase="onPurchaseToken"
    />
  </b-overlay>
</template>

<script>
import { canBuyTokens } from '@/utils/tokenAccess'
import { mapActions, mapGetters } from 'vuex'
import CandidateCard from '@/components/candidates/CandidateCard.vue'
import CandidatesTabs from '@/components/candidates/CandidatesTabs.vue'
import ResumeRequestModal from '@/components/modals/ResumeRequestModal.vue'
import WorkforceInvitationModal from '@/components/modals/WorkforceInvitationModal.vue'
import TokenConfirmationModal from '@/components/modals/TokenConfirmationModal'
import PurchaseTokenModal from '@/components/modals/PurchaseTokenModal'

export default {
  name: 'CandidatesInProgressPage',

  components: {
    CandidateCard,
    CandidatesTabs,
    ResumeRequestModal,
    WorkforceInvitationModal,
    TokenConfirmationModal,
    PurchaseTokenModal,
  },

  data() {
    return {
      isLoading: false,
      isRequestingResume: false,
      isInvitingWorkforce: false,
      candidateForModal: null,

      showTokenConfirmationModal: false,
      showPurchaseTokenModal: false,
      otwInvitationForModal: null,

      searchQuery: '',
      // The two terminal groups start collapsed — they're history, not a
      // to-do list.
      expandedSections: { you: true, them: true, done: false, closed: false },
    }
  },

  computed: {
    ...mapGetters('candidate', ['inProgressCandidates', 'favourites']),
    ...mapGetters('auth', ['user', 'hasPermission']),

    isFree() {
      return !!(this.user && this.user.is_free)
    },

    // Same permission pattern as NotificationsList.vue's token modals.
    canApplyOtwTokens() {
      return (
        (this.user && this.user.is_primary) ||
        this.hasPermission('candidates.add_to_workforce')
      )
    },

    canPurchaseTokens() {
      return canBuyTokens(this.user, this.hasPermission)
    },

    // Client-side only — this list is scoped to a company's own active
    // conversations (never large enough to warrant a server round-trip per
    // keystroke, unlike the main browse search on ListPage.vue).
    filteredInProgressCandidates() {
      const query = this.searchQuery.trim().toLowerCase()
      if (!query) return this.inProgressCandidates
      return this.inProgressCandidates.filter(
        (candidate) =>
          (candidate.name && candidate.name.toLowerCase().includes(query)) ||
          (candidate.trade && candidate.trade.toLowerCase().includes(query)),
      )
    },

    // Group the pipeline by whose turn it is, so "what do I need to do?" and
    // "what am I waiting on?" aren't jumbled into one list.
    pipelineSections() {
      const groups = { you: [], them: [], done: [], closed: [] }
      this.filteredInProgressCandidates.forEach((candidate) => {
        groups[this.stageOf(candidate)].push(candidate)
      })
      return [
        {
          key: 'you',
          title: 'Your move',
          hint: 'Needs an action from you',
          candidates: groups.you,
        },
        {
          key: 'them',
          title: 'Waiting on them',
          hint: 'Sent — awaiting their response',
          candidates: groups.them,
        },
        {
          key: 'done',
          title: 'Added to workforce',
          hint: 'Hired — manage them under Tradies',
          candidates: groups.done,
        },
        {
          key: 'closed',
          title: 'Not proceeding',
          hint: 'Declined or expired',
          candidates: groups.closed,
        },
      ]
    },

    visiblePipelineSections() {
      return this.pipelineSections.filter((s) => s.candidates.length)
    },
  },

  async created() {
    await this.loadAll()
    this.openTokenModalFromQuery()
  },

  watch: {
    // Notification bell -> onOtwWorkforceInvitationAcceptedClick() navigates
    // here with applyTokenInvitationId in the query to trigger this same
    // modal. created() alone only catches that on a fresh page load — if the
    // BA is already sitting on this page, Vue Router reuses this component
    // instance for a query-only navigation (no remount, so created() never
    // reruns), and the click silently did nothing. Watching the query field
    // itself catches that same-page case too, without double-firing the
    // initial load (a watcher doesn't run on mount unless immediate: true).
    '$route.query.applyTokenInvitationId'() {
      this.openTokenModalFromQuery()
    },
  },

  methods: {
    ...mapActions('candidate', [
      'fetchInProgressCandidates',
      'fetchFavourites',
      'applyToken',
      'dismissToken',
    ]),

    async loadAll() {
      this.isLoading = true
      try {
        await Promise.all([
          this.fetchInProgressCandidates(),
          this.fetchFavourites(),
        ])
      } catch (err) {
        this.onError(err)
      } finally {
        this.isLoading = false
      }
    },

    // Once a candidate's workforce invitation is genuinely 'active' (token
    // applied, accepted+non-archived company_tradie row), they're no longer
    // reachable via the candidate-profile route at all —
    // CandidateProfileController::show() deliberately excludes anyone
    // already an accepted member of the viewing company (same "already on
    // this company's workforce" guard CandidateSearchService::baseQuery()
    // uses), so that route 404s for them. They're viewed as a Tradie now,
    // not a candidate — route there instead.
    goToCandidate(id) {
      const candidate = this.findCandidate(id)
      if (candidate && candidate.workforce_invitation_status === 'active') {
        this.$router
          .push({ name: 'tradies.view-tradie', params: { tradieId: id } })
          .catch(() => {})
        return
      }
      this.$router.push({
        name: 'candidates.view-candidate',
        params: { candidateId: id },
      })
    },

    // Which pipeline bucket a candidate belongs in. Workforce phase takes
    // precedence over the resume phase (it's further down the funnel); within
    // each phase, "sent, awaiting a reply" is theirs and anything with a
    // button to click is yours.
    stageOf(c) {
      const r = c.resume_request_status
      const w = c.workforce_invitation_status
      if (w === 'active') return 'done'
      if (w === 'pending') return 'them'
      if (w === 'accepted' || w === 'token_pending') return 'you'
      if (w === 'declined') return 'you'
      if (r === 'pending') return 'them'
      if (r === 'accepted') return 'you'
      if (r === 'declined') return c.resume_request_retryable ? 'you' : 'closed'
      return 'closed'
    },

    toggleSection(key) {
      this.$set(this.expandedSections, key, !this.expandedSections[key])
    },

    // Looked up in either list — the same candidate can appear as both an
    // in-progress conversation and a favourite.
    findCandidate(tradieId) {
      return (
        this.inProgressCandidates.find((c) => c.id === tradieId) ||
        this.favourites.find((c) => c.id === tradieId) || { id: tradieId }
      )
    },

    openResumeRequestModal(tradieId) {
      this.candidateForModal = this.findCandidate(tradieId)
      this.isRequestingResume = true
    },

    openWorkforceInvitationModal(tradieId) {
      this.candidateForModal = this.findCandidate(tradieId)
      this.isInvitingWorkforce = true
    },

    onResumeRequestSent() {
      this.isRequestingResume = false
      this.showToast('Success', 'Resume request sent.')
      this.loadAll()
    },

    onWorkforceInvitationSent() {
      this.isInvitingWorkforce = false
      this.showToast('Success', 'Workforce invitation sent.')
      this.loadAll()
    },

    // { invitationId, tradieId, tradieName } from CandidateCard's
    // @apply-token — invitationId can be missing if the card data is stale
    // (e.g. a cached list from before this field existed); guard rather than
    // open a modal with nothing to act on.
    openTokenConfirmationModal({ invitationId, tradieId, tradieName }) {
      if (!invitationId) {
        this.onError({
          data: {
            message:
              'Could not find this invitation - try refreshing the page.',
          },
        })
        return
      }

      // Already holds a live token at this company — most commonly a
      // returning tradie (see CandidateSearchService::requestStatuses():
      // removeTradie() detaches company_tradie but never frees the token,
      // so it just stays assigned forever). Nothing to decide here — the
      // backend would just silently reuse it anyway (WorkforceInvitationService
      // ::deductToken()) — so skip prompting to "apply" a token that has no
      // real cost, and link it up directly instead.
      const candidate = this.findCandidate(tradieId)
      if (candidate && candidate.has_existing_token) {
        this.applyTokenForInvitation(invitationId)
        return
      }

      this.otwInvitationForModal = {
        id: invitationId,
        tradie_id: tradieId,
        tradie_name: tradieName,
      }
      this.showTokenConfirmationModal = true
    },

    onTokenConfirmationModalClose() {
      this.showTokenConfirmationModal = false
      this.otwInvitationForModal = null
    },

    // Landed here from the notification bell's "X accepted your workforce
    // invitation" row (NotificationsList.vue's onOtwWorkforceInvitationAcceptedClick
    // navigates here with these query params instead of popping the modal
    // in-place over whatever page the BA happened to be on) — open the same
    // modal this page's own CandidateCard @apply-token already uses, then
    // strip the params so a refresh or back-navigation doesn't reopen it.
    openTokenModalFromQuery() {
      const {
        applyTokenInvitationId,
        applyTokenTradieId,
        applyTokenTradieName,
      } = this.$route.query
      if (!applyTokenInvitationId) return

      this.openTokenConfirmationModal({
        invitationId: parseInt(applyTokenInvitationId, 10),
        tradieId: applyTokenTradieId ? parseInt(applyTokenTradieId, 10) : null,
        tradieName: applyTokenTradieName || null,
      })

      const {
        applyTokenInvitationId: _a,
        applyTokenTradieId: _b,
        applyTokenTradieName: _c,
        ...rest
      } = this.$route.query
      this.$router.replace({ query: rest }).catch(() => {})
    },

    async onApplyOtwToken() {
      if (!this.otwInvitationForModal) return
      const invitationId = this.otwInvitationForModal.id
      this.showTokenConfirmationModal = false
      this.otwInvitationForModal = null
      await this.applyTokenForInvitation(invitationId)
    },

    // Shared by the modal's own Apply Token button and
    // openTokenConfirmationModal()'s silent-reuse path (has_existing_token).
    async applyTokenForInvitation(invitationId) {
      try {
        const response = await this.applyToken(invitationId)
        await this.$store.dispatch('token/getBalance')
        // Set by WorkforceInvitationService::applyToken() when this tradie
        // already held an assigned token at this company (most commonly:
        // previously in the workforce, removed — which never frees their
        // token — then re-invited and re-accepted). Their existing token
        // was reused, not a new one, so say that plainly rather than the
        // generic "token applied" message, which would otherwise imply a
        // fresh one was just consumed.
        const reusedExisting =
          response && response.data && response.data.reused_existing_token
        let message = 'Token applied — candidate added to your workforce.'
        if (this.isFree) {
          // Free tier / trial: the API applied its free bypass, no token
          // involved — don't imply one was.
          message = 'Candidate added to your workforce.'
        } else if (reusedExisting) {
          message =
            'This candidate already had an active token from before — no new token was used.'
        }
        this.showToast('Success', message)
        this.loadAll()
      } catch (err) {
        this.onError(err)
      }
    },

    async onDismissOtwToken() {
      if (!this.otwInvitationForModal) return
      try {
        await this.dismissToken(this.otwInvitationForModal.id)
        this.showTokenConfirmationModal = false
        this.otwInvitationForModal = null
        this.showToast(
          'Saved',
          "We'll remind you - check back here to apply it later.",
        )
        this.loadAll()
      } catch (err) {
        this.onError(err)
      }
    },

    onOtwTokenModalPurchaseTokens() {
      this.showTokenConfirmationModal = false
      if (this.canPurchaseTokens) {
        this.showPurchaseTokenModal = true
      }
    },

    async onPurchaseToken(quantity) {
      if (!quantity || quantity < 1) return
      try {
        await this.$store.dispatch('token/purchase', quantity)
        await this.$store.dispatch('token/getBalance')
        this.showPurchaseTokenModal = false
        if (this.otwInvitationForModal) {
          this.showTokenConfirmationModal = true
        }
      } catch (err) {
        this.onError(err)
      }
    },

    onError(err) {
      const message =
        (err && err.data && err.data.message) ||
        (err && err.message) ||
        'Something went wrong.'
      this.showToast('Error', message, 'danger')
    },

    showToast(title, message, variant = 'success') {
      this.$bvToast.toast(message, {
        title,
        autoHideDelay: 3000,
        variant,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.in-progress-filters {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.in-progress-filters__search {
  flex: 1 1 260px;
  max-width: 420px;
}

.pipeline-section__header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.6rem 0.85rem;
  background: #f4f6f8;
  border: 1px solid #e1e6ea;
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s ease;
}

.pipeline-section__header:hover {
  background: #eef1f4;
}

.pipeline-section__header--you {
  background: #eef4ff;
  border-color: #cfe0ff;
}

.pipeline-section__header--you:hover {
  background: #e4eeff;
}

.pipeline-section__header--done {
  background: #edf7f0;
  border-color: #cfe9d8;
}

.pipeline-section__header--done:hover {
  background: #e3f2e9;
}

.pipeline-section__header--done .pipeline-section__count {
  background: #1e9e63;
}

.pipeline-section__chevron {
  flex-shrink: 0;
  color: #6c7581;
}

.pipeline-section__title {
  font-weight: 700;
  font-size: 0.95rem;
  color: #2b3440;
}

.pipeline-section__count {
  background: #6c7581;
  color: #fff;
  font-size: 0.72rem;
}

.pipeline-section__header--you .pipeline-section__count {
  background: #006cff;
}

.pipeline-section__hint {
  margin-left: auto;
  font-size: 0.78rem;
  color: #97a1ac;
  white-space: nowrap;
}

@media (max-width: 575.98px) {
  .pipeline-section__hint {
    display: none;
  }
}

// Bootstrap's default .row carries -15px side margins, meant to be soaked up
// by the 15px side padding on a normal parent column. This page's rows sit
// directly inside a global `.col-12` (see src/assets/scss/base/_animations.scss),
// which is force-zeroed to 0 side padding, so the row's negative margins
// bleed 15px past the viewport edge with nothing to cancel them — invisible
// on a wide desktop screen (silently clipped, no visible scrollbar) but a
// real horizontal-scroll bug from ~375px down to 320px.
.candidates-grid-row {
  margin-left: 0;
  margin-right: 0;
}
</style>
